import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Building2, LogOut, TrendingUp, TrendingDown, DollarSign, FileText, AlertCircle, Plus, Edit, Trash2, GripVertical, Settings as SettingsIcon, ClipboardList, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { businesses, reports, auth } from '../api';
import { useToast } from '../hooks/use-toast';
import { sessionManager } from '../utils/auth';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../utils/currency';
import Invoices from './Invoices';
import Estimates from './Estimates';
import Expenses from './Expenses';
import Reports from './Reports';
import Clients from './Clients';
import ProductsServices from './ProductsServices';
import OverheadAllocation from './OverheadAllocation';
import DivisionTransfers from './DivisionTransfers';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [businessList, setBusinessList] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState(null);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [newBusinessData, setNewBusinessData] = useState({ name: '', industry: '' });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [holdingDiagnostic, setHoldingDiagnostic] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);

  const runHoldingDiagnostic = async () => {
    if (!editingBusiness?.id) return;
    setDiagnosticLoading(true);
    try {
      const res = await businesses.holdingDiagnostic(editingBusiness.id);
      setHoldingDiagnostic(res.data);
    } catch (error) {
      console.error('Diagnostic failed:', error);
      toast({ title: 'Error', description: 'Diagnostic failed', variant: 'destructive' });
    } finally {
      setDiagnosticLoading(false);
    }
  };

  // Authentication check - redirect if no token
  useEffect(() => {
    const token = sessionManager.getToken();
    console.log('🔐 Dashboard auth check - token:', token ? 'present' : 'missing');
    if (!token) {
      console.log('🔐 No token found, redirecting to login');
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    fetchBusinesses();
  }, []);

  useEffect(() => {
    if (selectedBusiness) {
      fetchDashboardData();
    }
  }, [selectedBusiness]);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const response = await businesses.getAll();
      setBusinessList(response.data);
      if (response.data.length > 0) {
        setSelectedBusiness(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
      toast({
        title: 'Error',
        description: 'Failed to load businesses',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await reports.getDashboard(selectedBusiness);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    try {
      await businesses.create(newBusinessData);
      setIsCreateDialogOpen(false);
      setNewBusinessData({ name: '', industry: '' });
      await fetchBusinesses();
      toast({
        title: 'Success',
        description: 'Business created successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create business',
        variant: 'destructive'
      });
    }
  };

  const handleEditBusiness = (business) => {
    setEditingBusiness(business);
    setNewBusinessData({
      name: business.name,
      industry: business.industry,
      logo: business.logo || '',
      is_holding_company: !!business.is_holding_company,
      subsidiary_ids: business.subsidiary_ids || [],
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateBusiness = async (e) => {
    e.preventDefault();
    try {
      await businesses.update(editingBusiness.id, newBusinessData);
      setIsEditDialogOpen(false);
      setEditingBusiness(null);
      setNewBusinessData({ name: '', industry: '', logo: '' });
      await fetchBusinesses();
      toast({
        title: 'Success',
        description: 'Business updated successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update business',
        variant: 'destructive'
      });
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'Logo must be less than 2MB',
          variant: 'destructive'
        });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewBusinessData(prev => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setNewBusinessData(prev => ({ ...prev, logo: '' }));
  };

  const handleDeleteClick = (business) => {
    setBusinessToDelete(business);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await businesses.delete(businessToDelete.id);
      setIsDeleteDialogOpen(false);
      setBusinessToDelete(null);
      await fetchBusinesses();
      toast({
        title: 'Success',
        description: 'Business deleted successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete business',
        variant: 'destructive'
      });
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newBusinessList = [...businessList];
    const draggedItem = newBusinessList[draggedIndex];
    newBusinessList.splice(draggedIndex, 1);
    newBusinessList.splice(index, 0, draggedItem);
    
    setBusinessList(newBusinessList);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear session and redirect
      sessionManager.clearToken();
      window.location.href = '/login';
    }
  };

  const currentBusiness = businessList.find(b => b.id === selectedBusiness);

  const renderOverview = () => {
    if (!dashboardData) {
      return <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>;
    }
    const cur = currentBusiness?.currency || 'USD';

    return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-shadow duration-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{t('dashboard.total_revenue')}</CardTitle>
            <TrendingUp className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900" data-testid="stat-total-revenue">{formatMoney(dashboardData.totalRevenue, cur)}</div>
            <p className="text-xs text-green-600 mt-1">From paid invoices & deposits</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500 hover:shadow-lg transition-shadow duration-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{t('dashboard.total_expenses')}</CardTitle>
            <TrendingDown className="h-5 w-5 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900" data-testid="stat-total-expenses">{formatMoney(dashboardData.totalExpenses, cur)}</div>
            <p className="text-xs text-red-600 mt-1">All recorded expenses</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow duration-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{t('dashboard.net_profit')}</CardTitle>
            <DollarSign className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900" data-testid="stat-net-profit">{formatMoney(dashboardData.netProfit, cur)}</div>
            <p className="text-xs text-blue-600 mt-1">{dashboardData.totalRevenue > 0 ? ((dashboardData.netProfit / dashboardData.totalRevenue) * 100).toFixed(1) : 0}% margin</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500 hover:shadow-lg transition-shadow duration-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">{t('dashboard.pending_invoices')}</CardTitle>
            <FileText className="h-5 w-5 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900" data-testid="stat-pending-invoices">{formatMoney(dashboardData.pendingInvoices, cur)}</div>
            {dashboardData.overdueInvoices > 0 && (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {formatMoney(dashboardData.overdueInvoices, cur)} overdue
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="hover:shadow-lg transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">{t('dashboard.revenue_by_month')}</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.revenueByMonth && dashboardData.revenueByMonth.length > 0 ? (
              (() => {
                const maxRevenue = Math.max(...dashboardData.revenueByMonth.map(item => item.amount), 1);
                return (
                  <div className="space-y-3">
                    {dashboardData.revenueByMonth.map((item, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 font-medium w-12">{item.month}</span>
                        <div className="flex-1 mx-4">
                          <div className="h-8 bg-gradient-to-r from-blue-100 to-blue-200 rounded-lg relative overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg transition-all duration-500"
                              style={{ width: `${(item.amount / maxRevenue) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-gray-900 w-24 text-right">{formatMoney(item.amount, cur)}</span>
                      </div>
                    ))}
                  </div>
                );
              })()
            ) : (
              <p className="text-center text-gray-500">{t('common.no_data')}</p>
            )}
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">{t('dashboard.expenses_by_category')}</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.expensesByCategory && dashboardData.expensesByCategory.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.expensesByCategory.map((item, index) => {
                  const colors = ['red', 'blue', 'green', 'yellow', 'purple', 'pink'];
                  const color = colors[index % colors.length];
                  return (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 font-medium w-32 truncate">{item.category}</span>
                      <div className="flex-1 mx-4">
                        <div className="h-8 bg-gray-100 rounded-lg relative overflow-hidden">
                          <div
                            className={`h-full bg-gradient-to-r from-${color}-400 to-${color}-500 rounded-lg transition-all duration-500`}
                            style={{ width: `${Math.min((item.amount / 20000) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 w-20 text-right">{formatMoney(item.amount, cur)}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-gray-500">{t('common.no_data')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Legal Entity: Divisional breakdown + Consolidated recent lists */}
      {dashboardData.is_holding_company && (
        <>
          <Card className="border-l-4 border-l-blue-600" data-testid="holding-subsidiary-summary">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                Consolidated by Division
              </CardTitle>
              <span className="text-xs text-gray-500">
                Totals shown in {dashboardData.currency}. Each row uses its own native currency. Internal management view — divisions are operating units of the legal entity.
              </span>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-gray-600 border-b">
                    <tr>
                      <th className="pb-2">Division</th>
                      <th className="pb-2 text-right">Revenue</th>
                      <th className="pb-2 text-right">Expenses</th>
                      <th className="pb-2 text-right">Net P&amp;L</th>
                      <th className="pb-2 text-right">Allocation</th>
                      <th className="pb-2 text-right">Net After Alloc.</th>
                      <th className="pb-2 text-right">Pending</th>
                      <th className="pb-2 text-right">Overdue</th>
                      <th className="pb-2 text-center">Invoices</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(dashboardData.subsidiary_summaries || []).map((s) => (
                      <tr key={s.id} className="hover:bg-gray-50" data-testid={`sub-summary-${s.id}`}>
                        <td className="py-2">
                          <div className="font-medium text-gray-900">
                            {s.display_name || s.name}
                            {s.is_corporate_head_office && (
                              <span className="ml-2 text-[10px] uppercase font-bold bg-blue-200 text-blue-900 px-1.5 py-0.5 rounded">Corporate</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">{s.currency}</div>
                        </td>
                        <td className="py-2 text-right font-mono text-green-700">{formatMoney(s.revenue, s.currency)}</td>
                        <td className="py-2 text-right font-mono text-red-700">{formatMoney(s.expenses, s.currency)}</td>
                        <td className={`py-2 text-right font-mono font-semibold ${s.net_profit >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{formatMoney(s.net_profit, s.currency)}</td>
                        <td className="py-2 text-right font-mono text-orange-700">{formatMoney(s.corporate_overhead_allocation || 0, s.currency)}</td>
                        <td className={`py-2 text-right font-mono font-semibold ${(s.net_profit_after_allocation ?? s.net_profit) >= 0 ? 'text-blue-800' : 'text-red-800'}`}>{formatMoney(s.net_profit_after_allocation ?? s.net_profit, s.currency)}</td>
                        <td className="py-2 text-right font-mono text-yellow-700">{formatMoney(s.pending, s.currency)}</td>
                        <td className="py-2 text-right font-mono text-red-700">{formatMoney(s.overdue, s.currency)}</td>
                        <td className="py-2 text-center text-gray-700">{s.invoice_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {dashboardData.per_currency_breakdown && Object.keys(dashboardData.per_currency_breakdown).length > 1 && (
                <div className="mt-4 border-t pt-3">
                  <div className="text-xs font-semibold text-gray-600 mb-2">Per-Currency Breakdown</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(dashboardData.per_currency_breakdown).map(([cur, vals]) => (
                      <div key={cur} className="text-xs px-3 py-1.5 bg-gray-100 rounded border border-gray-200">
                        <span className="font-bold text-gray-800">{cur}</span>
                        <span className="text-green-700 ml-2">Rev: {formatMoney(vals.revenue, cur)}</span>
                        <span className="text-red-700 ml-2">Exp: {formatMoney(vals.expenses, cur)}</span>
                        <span className={`ml-2 font-semibold ${vals.net_profit >= 0 ? 'text-blue-700' : 'text-red-700'}`}>Net: {formatMoney(vals.net_profit, cur)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-gray-800">Recent Invoices (All Divisions)</CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboardData.consolidated_recent_invoices || []).length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-4">{t('common.no_data')}</p>
                ) : (
                  <div className="space-y-2">
                    {dashboardData.consolidated_recent_invoices.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between border-b pb-2" data-testid={`consolidated-inv-${inv.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{inv.client_name || '—'}</div>
                          <div className="text-xs text-gray-500 flex gap-2">
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold">{inv._business_name}</span>
                            <span>{inv.invoice_number}</span>
                            <span>{inv.issue_date}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-gray-900">{formatMoney(inv.amount, inv._business_currency)}</div>
                          <div className={`text-[10px] uppercase font-semibold ${
                            inv.status === 'paid' ? 'text-green-600' :
                            inv.status === 'overdue' ? 'text-red-600' : 'text-yellow-600'
                          }`}>{inv.status}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-gray-800">Recent Expenses (All Divisions)</CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboardData.consolidated_recent_expenses || []).length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-4">{t('common.no_data')}</p>
                ) : (
                  <div className="space-y-2">
                    {dashboardData.consolidated_recent_expenses.map((exp) => (
                      <div key={exp.id} className="flex items-center justify-between border-b pb-2" data-testid={`consolidated-exp-${exp.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{exp.description || exp.category || '—'}</div>
                          <div className="text-xs text-gray-500 flex gap-2">
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold">{exp._business_name}</span>
                            <span>{exp.category}</span>
                            <span>{exp.date}</span>
                          </div>
                        </div>
                        <div className="text-right text-sm font-semibold text-red-700">{formatMoney(exp.amount, exp._business_currency)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  )};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-red-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (businessList.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-red-50">
        <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-blue-600" />
                <h1 className="text-2xl font-bold">
                  <span className="text-red-600">Turbo</span>
                  <span className="text-blue-600">Books</span>
                </h1>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500">{user?.email || 'user@example.com'}</p>
                </div>
                {user?.picture && (
                  <img src={user.picture} alt="Profile" className="w-10 h-10 rounded-full border-2 border-blue-500" />
                )}
                <Button onClick={handleLogout} variant="outline" size="sm" className="gap-2">
                  <LogOut className="h-4 w-4" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </header>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Building2 className="w-24 h-24 text-blue-600 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Welcome to TurboBooks!</h2>
          <p className="text-lg text-gray-600 mb-8">Get started by creating your first business</p>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 gap-2 text-lg px-8 py-6">
                <Plus className="h-5 w-5" />
                Create Your First Business
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Business</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateBusiness} className="space-y-4">
                <div>
                  <Label htmlFor="name">Business Name *</Label>
                  <Input
                    id="name"
                    value={newBusinessData.name}
                    onChange={(e) => setNewBusinessData({ ...newBusinessData, name: e.target.value })}
                    required
                    placeholder="Enter business name"
                  />
                </div>
                <div>
                  <Label htmlFor="industry">Industry *</Label>
                  <Input
                    id="industry"
                    value={newBusinessData.industry}
                    onChange={(e) => setNewBusinessData({ ...newBusinessData, industry: e.target.value })}
                    required
                    placeholder="e.g., Technology, Retail, Consulting"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Create Business
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-red-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-blue-600" />
              <h1 className="text-2xl font-bold">
                <span className="text-red-600">Turbo</span>
                <span className="text-blue-600">Books</span>
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <div className="text-right hidden sm:block min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email || 'user@example.com'}</p>
              </div>
              {user?.picture && (
                <img src={user.picture} alt="Profile" className="w-10 h-10 rounded-full border-2 border-blue-500" />
              )}
              <Button onClick={handleLogout} variant="outline" size="sm" className="gap-2 shrink-0" data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Select Business</h2>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 gap-2">
                <Plus className="h-4 w-4" />
                New Business
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Business</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateBusiness} className="space-y-4">
                <div>
                  <Label htmlFor="name">Business Name *</Label>
                  <Input
                    id="name"
                    value={newBusinessData.name}
                    onChange={(e) => setNewBusinessData({ ...newBusinessData, name: e.target.value })}
                    required
                    placeholder="Enter business name"
                  />
                </div>
                <div>
                  <Label htmlFor="industry">Industry *</Label>
                  <Input
                    id="industry"
                    value={newBusinessData.industry}
                    onChange={(e) => setNewBusinessData({ ...newBusinessData, industry: e.target.value })}
                    required
                    placeholder="e.g., Technology, Retail, Consulting"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Create Business
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mb-6">
          <div className="bg-white border border-gray-200 p-4 rounded-lg">
            <div className="flex items-center gap-2 flex-wrap">
              {businessList.map((business, index) => (
                <div
                  key={business.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-move transition-all duration-200 ${
                    selectedBusiness === business.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setSelectedBusiness(business.id)}
                >
                  <GripVertical className="h-4 w-4 opacity-50" />
                  <span className="font-medium">{business.name}</span>
                  {business.is_holding_company && (
                    <span
                      className={`px-1.5 py-0.5 text-[9px] font-bold rounded uppercase tracking-wide ${
                        selectedBusiness === business.id ? 'bg-yellow-300 text-blue-900' : 'bg-blue-600 text-white'
                      }`}
                      title="Legal Entity (aggregates divisions)"
                      data-testid={`holding-badge-${business.id}`}
                    >
                      Legal Entity
                    </span>
                  )}
                  <div className="flex gap-1 ml-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className={`h-6 w-6 p-0 ${selectedBusiness === business.id ? 'hover:bg-blue-500' : 'hover:bg-gray-300'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditBusiness(business);
                      }}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className={`h-6 w-6 p-0 text-red-600 ${selectedBusiness === business.id ? 'hover:bg-blue-500' : 'hover:bg-red-50'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(business);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Edit Business Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Business</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateBusiness} className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Business Name *</Label>
                <Input
                  id="edit-name"
                  value={newBusinessData.name}
                  onChange={(e) => setNewBusinessData({ ...newBusinessData, name: e.target.value })}
                  required
                  placeholder="Enter business name"
                />
              </div>
              <div>
                <Label htmlFor="edit-industry">Industry *</Label>
                <Input
                  id="edit-industry"
                  value={newBusinessData.industry}
                  onChange={(e) => setNewBusinessData({ ...newBusinessData, industry: e.target.value })}
                  required
                  placeholder="e.g., Technology, Retail, Consulting"
                />
              </div>
              
              {/* Logo Upload */}
              <div>
                <Label>Business Logo</Label>
                <p className="text-xs text-gray-500 mb-2">Upload a logo to appear on invoices (max 2MB)</p>
                <div className="flex items-center gap-4">
                  {newBusinessData.logo ? (
                    <div className="relative">
                      <img 
                        src={newBusinessData.logo} 
                        alt="Business logo" 
                        className="w-20 h-20 object-contain border rounded-lg bg-gray-50"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                        onClick={removeLogo}
                      >
                        ×
                      </Button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 border-2 border-dashed rounded-lg flex items-center justify-center text-gray-400">
                      <Building2 className="h-8 w-8" />
                    </div>
                  )}
                  <div>
                    <Input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('logo-upload').click()}
                    >
                      {newBusinessData.logo ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="is-holding-company"
                    checked={!!newBusinessData.is_holding_company}
                    onChange={(e) => setNewBusinessData({ ...newBusinessData, is_holding_company: e.target.checked, subsidiary_ids: e.target.checked ? (newBusinessData.subsidiary_ids || []) : [] })}
                    className="mt-1 h-4 w-4 accent-blue-600"
                    data-testid="holding-company-toggle"
                  />
                  <div className="flex-1">
                    <Label htmlFor="is-holding-company" className="cursor-pointer font-medium">
                      Mark as Legal Entity (with Divisions)
                    </Label>
                    <p className="text-xs text-gray-500 mt-0.5">Aggregate revenue, expenses, P&amp;L, and outstanding invoices from selected operating divisions. Divisions are internal operating units of this legal company, not separate subsidiaries.</p>
                  </div>
                </div>
                {newBusinessData.is_holding_company && (
                  <div className="mt-3 ml-7 space-y-2">
                    <Label className="text-sm">Select Operating Divisions</Label>
                    <div className="border rounded-lg divide-y max-h-56 overflow-y-auto bg-gray-50">
                      {businessList
                        .filter(b => b.id !== editingBusiness?.id && !b.is_holding_company)
                        .map(b => {
                          const checked = (newBusinessData.subsidiary_ids || []).includes(b.id);
                          return (
                            <label
                              key={b.id}
                              className="flex items-center gap-3 px-3 py-2 hover:bg-blue-50 cursor-pointer"
                              data-testid={`sub-choice-${b.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const list = new Set(newBusinessData.subsidiary_ids || []);
                                  if (e.target.checked) list.add(b.id); else list.delete(b.id);
                                  setNewBusinessData({ ...newBusinessData, subsidiary_ids: Array.from(list) });
                                }}
                                className="h-4 w-4 accent-blue-600"
                              />
                              <span className="flex-1 text-sm">{b.name}</span>
                              <span className="text-xs text-gray-500">{b.currency || 'USD'}</span>
                            </label>
                          );
                        })}
                      {businessList.filter(b => b.id !== editingBusiness?.id && !b.is_holding_company).length === 0 && (
                        <p className="p-4 text-xs text-gray-400 text-center">No other eligible businesses. Create more businesses to add them as divisions.</p>
                      )}
                    </div>

                    {/* Diagnostic tool */}
                    <div className="pt-3 border-t border-dashed border-gray-300">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Not seeing the right totals? Run a diagnostic:</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={runHoldingDiagnostic}
                          disabled={diagnosticLoading}
                          data-testid="run-holding-diagnostic"
                        >
                          {diagnosticLoading ? 'Running...' : 'Run Diagnostic'}
                        </Button>
                      </div>
                      {holdingDiagnostic && (
                        <div className="mt-3 border rounded-lg bg-yellow-50 p-3 text-xs" data-testid="holding-diagnostic-result">
                          <div className="font-semibold mb-2">
                            Parent: {holdingDiagnostic.parent.name} · is_holding: {String(holdingDiagnostic.parent.is_holding_company)}
                          </div>
                          <div className="mb-2">
                            Configured Subsidiaries: {holdingDiagnostic.subsidiary_ids_configured.length} · Found: {holdingDiagnostic.subsidiaries_found_count}
                          </div>
                          {holdingDiagnostic.missing_subsidiary_ids?.length > 0 && (
                            <div className="mb-2 text-red-600">
                              ⚠ Missing sub IDs (deleted?): {holdingDiagnostic.missing_subsidiary_ids.join(', ')}
                            </div>
                          )}
                          <table className="w-full text-[11px] mt-2">
                            <thead className="text-gray-600">
                              <tr>
                                <th className="text-left">Business</th>
                                <th className="text-right">Invs</th>
                                <th className="text-right">Paid $</th>
                                <th className="text-right">Exp Ct</th>
                                <th className="text-right">Exp $</th>
                                <th className="text-right">CI Ct</th>
                                <th className="text-right">CI $</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {holdingDiagnostic.per_business.map((b) => (
                                <tr key={b.id}>
                                  <td className="py-1">{b.name} <span className="text-gray-400">({b.currency})</span></td>
                                  <td className="py-1 text-right">{b.invoice_count}</td>
                                  <td className="py-1 text-right font-mono">${(b.paid_invoice_sum || 0).toLocaleString()}</td>
                                  <td className="py-1 text-right">{b.expense_count}</td>
                                  <td className="py-1 text-right font-mono">${(b.expense_sum || 0).toLocaleString()}</td>
                                  <td className="py-1 text-right">{b.cash_injection_count}</td>
                                  <td className="py-1 text-right font-mono">${(b.cash_injection_sum || 0).toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <p className="mt-2 text-gray-500 italic">
                            If a subsidiary shows 0 invoices/expenses here, its data is stored under a different business_id in the DB.
                            The holding aggregation can only sum what's actually linked to each subsidiary's business_id.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingBusiness(null);
                  setNewBusinessData({ name: '', industry: '', logo: '' });
                }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Update Business
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{businessToDelete?.name}</strong> and all associated invoices and expenses. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => {
                setIsDeleteDialogOpen(false);
                setBusinessToDelete(null);
              }}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Business
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Card className="mb-6 bg-white shadow-md">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="relative flex-1 min-w-0">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="flex w-full justify-start overflow-x-auto md:grid md:overflow-visible bg-gray-100 p-1 rounded-lg [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" style={{ gridTemplateColumns: `repeat(${currentBusiness?.is_holding_company ? 9 : 7}, minmax(0, 1fr))` }} data-testid="dashboard-tabs-list">
                  <TabsTrigger value="overview" className="shrink-0 data-[state=active]:bg-white data-[state=active]:shadow-sm">Overview</TabsTrigger>
                  <TabsTrigger value="invoices" className="shrink-0 data-[state=active]:bg-white data-[state=active]:shadow-sm">Invoices</TabsTrigger>
                  <TabsTrigger value="estimates" className="shrink-0 data-[state=active]:bg-white data-[state=active]:shadow-sm">Estimates</TabsTrigger>
                  <TabsTrigger value="expenses" className="shrink-0 data-[state=active]:bg-white data-[state=active]:shadow-sm">Expenses</TabsTrigger>
                  <TabsTrigger value="clients" className="shrink-0 data-[state=active]:bg-white data-[state=active]:shadow-sm">Clients</TabsTrigger>
                  <TabsTrigger value="products" className="shrink-0 data-[state=active]:bg-white data-[state=active]:shadow-sm">
                    <Package className="h-4 w-4 mr-1 hidden sm:inline" />
                    Products
                  </TabsTrigger>
                  <TabsTrigger value="reports" className="shrink-0 data-[state=active]:bg-white data-[state=active]:shadow-sm">Reports</TabsTrigger>
                  {currentBusiness?.is_holding_company && (
                    <TabsTrigger value="allocations" className="shrink-0 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="allocations-tab-trigger">Allocations</TabsTrigger>
                  )}
                  {currentBusiness?.is_holding_company && (
                    <TabsTrigger value="transfers" className="shrink-0 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="transfers-tab-trigger">Transfers</TabsTrigger>
                  )}
                </TabsList>
                </Tabs>
                <div className="pointer-events-none absolute inset-y-0 right-0 w-10 rounded-r-lg bg-gradient-to-l from-white to-transparent md:hidden" aria-hidden="true" />
              </div>
              <Button 
                variant="outline" 
                onClick={() => navigate('/settings')}
                className="gap-2 shrink-0 px-2.5 sm:px-4"
                data-testid="settings-btn"
              >
                <SettingsIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Settings</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'invoices' && <Invoices businessId={selectedBusiness} businessName={currentBusiness?.name} businessCurrency={currentBusiness?.currency || 'USD'} onUpdate={fetchDashboardData} />}
          {activeTab === 'estimates' && <Estimates businessId={selectedBusiness} businessName={currentBusiness?.name} businessCurrency={currentBusiness?.currency || 'USD'} />}
          {activeTab === 'expenses' && <Expenses businessId={selectedBusiness} businessName={currentBusiness?.name} businessCurrency={currentBusiness?.currency || 'USD'} onUpdate={fetchDashboardData} />}
          {activeTab === 'clients' && <Clients businessId={selectedBusiness} businessName={currentBusiness?.name} />}
          {activeTab === 'products' && <ProductsServices businessId={selectedBusiness} businessCurrency={currentBusiness?.currency || 'USD'} onUpdate={fetchDashboardData} />}
          {activeTab === 'reports' && <Reports businessId={selectedBusiness} businessName={currentBusiness?.name} businessCurrency={currentBusiness?.currency || 'USD'} dashboardData={dashboardData} />}
          {activeTab === 'allocations' && currentBusiness?.is_holding_company && <OverheadAllocation businessId={selectedBusiness} businessName={currentBusiness?.name} businessCurrency={currentBusiness?.currency || 'USD'} />}
          {activeTab === 'transfers' && currentBusiness?.is_holding_company && <DivisionTransfers businessId={selectedBusiness} businessName={currentBusiness?.name} businessCurrency={currentBusiness?.currency || 'USD'} />}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
